import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { RefreshCw, LogOut, Target, AlertTriangle, MessageCircle, Send, Trash2 } from 'lucide-react';
import { Navigation } from './Navigation';

// SectionCard component
const SectionCard = ({ title, children }) => (
  <div className="bg-white/5 rounded-xl p-4">
    <h3 className="text-sm font-medium text-white/60 mb-3 pb-2 border-b border-white/10">{title}</h3>
    {children}
  </div>
);

// Comment component
const Comment = ({ comment, onDelete, currentUserEmail }) => {
  const isOwner = comment.user_email === currentUserEmail;
  const date = new Date(comment.created_at);
  const formattedDate = date.toLocaleDateString('da-DK', { 
    day: 'numeric', 
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="flex gap-3 py-2">
      <div className="w-8 h-8 rounded-full bg-[#1E6F7A] flex items-center justify-center flex-shrink-0">
        <span className="text-xs text-white font-medium">
          {comment.user_name?.charAt(0)?.toUpperCase() || '?'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{comment.user_name}</span>
          <span className="text-xs text-white/40">{formattedDate}</span>
          {isOwner && (
            <button 
              onClick={() => onDelete(comment.created_at)}
              className="ml-auto p-1 text-white/30 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        <p className="text-sm text-white/80 mt-1">{comment.comment}</p>
      </div>
    </div>
  );
};

// Warning with comments component
const WarningWithComments = ({ warningId, message, currentUserEmail }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await api.get(`/kpi/comments/${encodeURIComponent(warningId)}`);
      setComments(res.data.comments || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  }, [warningId]);

  useEffect(() => {
    if (showComments) {
      fetchComments();
    }
  }, [showComments, fetchComments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setLoading(true);
    try {
      const res = await api.post('/kpi/comments', {
        warning_id: warningId,
        comment: newComment.trim()
      });
      
      if (res.data.success) {
        setComments(prev => [...prev, res.data.comment]);
        setNewComment('');
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/kpi/comments/${encodeURIComponent(commentId)}`);
      setComments(prev => prev.filter(c => c.created_at !== commentId));
    } catch (err) {
      console.error('Error deleting comment:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  return (
    <div className="space-y-3">
      {/* Warning */}
      <div className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-red-400">{message}</p>
        </div>
        <button
          onClick={() => setShowComments(!showComments)}
          className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
            showComments ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          <span>{comments.length || ''}</span>
        </button>
      </div>

      {/* Comments section */}
      {showComments && (
        <div className="ml-8 p-3 bg-white/5 rounded-lg border border-white/10">
          {/* Comments list */}
          {comments.length > 0 ? (
            <div className="space-y-2 mb-3 max-h-60 overflow-y-auto">
              {comments.map((comment, idx) => (
                <Comment 
                  key={comment.created_at || idx}
                  comment={comment}
                  onDelete={handleDeleteComment}
                  currentUserEmail={currentUserEmail}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-white/40 mb-3">Ingen kommentarer endnu</p>
          )}

          {/* Add comment input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Skriv en kommentar..."
              className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#1E6F7A]"
            />
            <button
              onClick={handleAddComment}
              disabled={loading || !newComment.trim()}
              className="px-3 py-2 bg-[#1E6F7A] text-white rounded-lg hover:bg-[#245d66] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const KPIer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingWarning, setBookingWarning] = useState(null);
  const [warningId, setWarningId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  const fetchData = useCallback(async () => {
    try {
      // Fetch user info and booking data
      const [userRes, bookingsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/bookings/new-this-week')
      ]);
      
      setCurrentUserEmail(userRes.data?.email || '');
      
      const data = bookingsRes.data;
      
      // Check if any completed day didn't meet the daily target
      const dailyTarget = data.daily_target || 36;
      const dailyData = data.daily_data || [];
      
      // Find days that didn't meet target (only completed days, not today)
      const today = new Date().toISOString().split('T')[0];
      const missedDays = dailyData.filter(day => 
        day.dato !== today && 
        day.nye_bookinger !== null && 
        day.nye_bookinger < dailyTarget
      );
      
      if (missedDays.length >= 1) {
        const lastMissedDay = missedDays[missedDays.length - 1];
        const warning = `${lastMissedDay.dag} nåede kun ${lastMissedDay.nye_bookinger} bookinger (mål: ${Math.round(dailyTarget)})`;
        setBookingWarning(warning);
        // Create a unique ID for this warning based on the date
        setWarningId(`booking-daily-${lastMissedDay.dato}`);
      } else {
        setBookingWarning(null);
        setWarningId(null);
      }
    } catch (err) {
      console.error('KPI data fetch error:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('session_token');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#121212' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E6F7A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-6" style={{ backgroundColor: '#121212' }}>
      {/* Header */}
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Navigation currentPage="kpier" />
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
              <Target className="w-6 h-6 text-[#1E6F7A]" />
              KPIer
            </h1>
            <p className="text-xs text-white/40 mt-1">Nøgletal og målinger</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg bg-[#1A1A1A] text-white hover:bg-[#252525] transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* KPI Sections */}
      <div className="space-y-6">
        {/* Økonomi */}
        <SectionCard title="Økonomi">
          <p className="text-white/40 text-sm">Kommer snart...</p>
        </SectionCard>

        {/* Bookinger */}
        <SectionCard title="Bookinger">
          {bookingWarning && warningId ? (
            <WarningWithComments 
              warningId={warningId}
              message={bookingWarning}
              currentUserEmail={currentUserEmail}
            />
          ) : (
            <p className="text-white/40 text-sm">Ingen advarsler</p>
          )}
        </SectionCard>

        {/* Konsulenter */}
        <SectionCard title="Konsulenter">
          <p className="text-white/40 text-sm">Kommer snart...</p>
        </SectionCard>
      </div>
    </div>
  );
};

export default KPIer;
